<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import AutoReplyModal from './AutoReplyModal.vue';
import TestPreviewModal from './TestPreviewModal.vue';
import InstagramAutoReplyAPI from '../../../../api/instagramAutoReply';

defineOptions({
  name: 'InstagramAutoReplySettings',
});

const { t } = useI18n();

const rules = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const showAddEditModal = ref(false);
const showTestModal = ref(false);
const selectedRule = ref(null);
const showDeleteModal = ref(false);
const ruleToDelete = ref(null);

const tableHeaders = computed(() => [
  t('INSTAGRAM_AUTO_REPLY.LIST.TABLE_HEADER.NAME'),
  t('INSTAGRAM_AUTO_REPLY.LIST.TABLE_HEADER.INBOX'),
  t('INSTAGRAM_AUTO_REPLY.LIST.TABLE_HEADER.KEYWORDS'),
  t('INSTAGRAM_AUTO_REPLY.LIST.TABLE_HEADER.REPLY'),
  t('INSTAGRAM_AUTO_REPLY.LIST.TABLE_HEADER.USAGE'),
  t('INSTAGRAM_AUTO_REPLY.LIST.TABLE_HEADER.STATUS'),
  t('INSTAGRAM_AUTO_REPLY.LIST.TABLE_HEADER.ACTIONS'),
]);

const fetchRules = async () => {
  loading.value = true;
  try {
    const res = await InstagramAutoReplyAPI.get();
    rules.value = res.data || [];
  } catch (error) {
    useAlert(t('INSTAGRAM_AUTO_REPLY.ERROR_FETCHING'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchRules();
});

const filteredRules = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return rules.value;
  return rules.value.filter(rule => {
    return (
      rule.name?.toLowerCase().includes(query) ||
      rule.trigger_keywords?.toLowerCase().includes(query) ||
      rule.reply_text?.toLowerCase().includes(query) ||
      rule.dm_text?.toLowerCase().includes(query)
    );
  });
});

const openAddModal = () => {
  selectedRule.value = null;
  showAddEditModal.value = true;
};

const openEditModal = rule => {
  selectedRule.value = { ...rule };
  showAddEditModal.value = true;
};

const openTestModal = () => {
  showTestModal.value = true;
};

const confirmDelete = rule => {
  ruleToDelete.value = rule;
  showDeleteModal.value = true;
};

const handleDelete = async () => {
  if (!ruleToDelete.value) return;

  try {
    await InstagramAutoReplyAPI.delete(ruleToDelete.value.id);
    useAlert(t('INSTAGRAM_AUTO_REPLY.DELETE.SUCCESS_MESSAGE'));
    showDeleteModal.value = false;
    ruleToDelete.value = null;
    fetchRules();
  } catch (error) {
    useAlert(t('INSTAGRAM_AUTO_REPLY.DELETE.ERROR_MESSAGE'));
  }
};

const toggleActive = async rule => {
  const newStatus = !rule.is_active;
  try {
    await InstagramAutoReplyAPI.update(rule.id, {
      instagram_auto_reply: { is_active: newStatus },
    });
    rule.is_active = newStatus;
    useAlert(
      newStatus
        ? t('INSTAGRAM_AUTO_REPLY.STATUS_ACTIVATED')
        : t('INSTAGRAM_AUTO_REPLY.STATUS_DEACTIVATED')
    );
  } catch (error) {
    useAlert(t('INSTAGRAM_AUTO_REPLY.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('INSTAGRAM_AUTO_REPLY.LOADING')"
    :no-records-found="!filteredRules.length"
    :no-records-message="$t('INSTAGRAM_AUTO_REPLY.NO_RECORDS')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INSTAGRAM_AUTO_REPLY.HEADER.TITLE')"
        :description="$t('INSTAGRAM_AUTO_REPLY.HEADER.DESCRIPTION')"
        :search-placeholder="$t('INSTAGRAM_AUTO_REPLY.HEADER.SEARCH_PLACEHOLDER')"
      >
        <template #actions>
          <div class="flex items-center gap-2">
            <Button
              faded
              slate
              icon="i-lucide-play-circle"
              :label="$t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.OPEN_BUTTON')"
              @click="openTestModal"
            />
            <Button
              icon="i-lucide-plus"
              :label="$t('INSTAGRAM_AUTO_REPLY.ADD.BUTTON_TEXT')"
              @click="openAddModal"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRules"
        :no-data-message="$t('INSTAGRAM_AUTO_REPLY.NO_RECORDS')"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="rule in items"
            :key="rule.id"
            :item="rule"
          >
            <template #default>
              <!-- Name & Priority -->
              <BaseTableCell>
                <div class="flex flex-col">
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-slate-900 dark:text-slate-100">
                      {{ rule.name }}
                    </span>
                    <span
                      v-if="rule.is_default"
                      class="px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                    >
                      {{ $t('INSTAGRAM_AUTO_REPLY.LIST.DEFAULT_BADGE') }}
                    </span>
                  </div>
                  <span class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {{ $t('INSTAGRAM_AUTO_REPLY.LIST.PRIORITY') }}: {{ rule.priority }}
                    <template v-if="rule.post_id">
                      | {{ $t('INSTAGRAM_AUTO_REPLY.LIST.POST_ID') }}: {{ rule.post_id }}
                    </template>
                  </span>
                </div>
              </BaseTableCell>

              <!-- Inbox -->
              <BaseTableCell>
                <span class="text-xs text-slate-700 dark:text-slate-300">
                  {{ rule.inbox?.name || $t('INSTAGRAM_AUTO_REPLY.FORM.INBOX.ALL_INBOXES') }}
                </span>
              </BaseTableCell>

              <!-- Triggers -->
              <BaseTableCell>
                <div class="flex flex-col gap-1 max-w-[200px]">
                  <span
                    v-if="rule.trigger_keywords"
                    class="text-xs text-slate-600 dark:text-slate-300 truncate"
                    :title="rule.trigger_keywords"
                  >
                    🔑 {{ rule.trigger_keywords }}
                  </span>
                  <span
                    v-if="rule.trigger_regex"
                    class="text-xs text-slate-500 font-mono truncate"
                    :title="rule.trigger_regex"
                  >
                    ⚙️ {{ rule.trigger_regex }}
                  </span>
                  <span
                    v-if="!rule.trigger_keywords && !rule.trigger_regex && rule.is_default"
                    class="text-xs italic text-amber-600 dark:text-amber-400"
                  >
                    {{ $t('INSTAGRAM_AUTO_REPLY.LIST.MATCHES_ANY') }}
                  </span>
                </div>
              </BaseTableCell>

              <!-- Reply Preview -->
              <BaseTableCell>
                <div class="flex flex-col gap-1 max-w-[220px]">
                  <span
                    class="text-xs text-slate-700 dark:text-slate-200 truncate"
                    :title="rule.reply_text"
                  >
                    💬 {{ rule.reply_text }}
                  </span>
                  <span
                    v-if="rule.dm_text"
                    class="text-xs text-blue-600 dark:text-blue-400 truncate"
                    :title="rule.dm_text"
                  >
                    📩 {{ rule.dm_text }}
                  </span>
                </div>
              </BaseTableCell>

              <!-- Usage -->
              <BaseTableCell>
                <span class="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  {{ rule.usage_count || 0 }}
                </span>
              </BaseTableCell>

              <!-- Status Toggle -->
              <BaseTableCell>
                <button
                  type="button"
                  class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                  :class="rule.is_active ? 'bg-woot-500' : 'bg-slate-300 dark:bg-slate-700'"
                  @click="toggleActive(rule)"
                >
                  <span
                    class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    :class="rule.is_active ? 'translate-x-4' : 'translate-x-0'"
                  />
                </button>
              </BaseTableCell>

              <!-- Actions -->
              <BaseTableCell align="end">
                <div class="flex items-center justify-end gap-1">
                  <Button
                    faded
                    slate
                    icon="i-lucide-pencil"
                    size="sm"
                    @click="openEditModal(rule)"
                  />
                  <Button
                    faded
                    ruby
                    icon="i-lucide-trash-2"
                    size="sm"
                    @click="confirmDelete(rule)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddEditModal" @close="showAddEditModal = false">
      <AutoReplyModal
        v-if="showAddEditModal"
        :rule="selectedRule"
        @close="showAddEditModal = false"
        @saved="fetchRules"
      />
    </woot-modal>

    <woot-modal v-model:show="showTestModal" @close="showTestModal = false">
      <TestPreviewModal
        v-if="showTestModal"
        @close="showTestModal = false"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-confirm="handleDelete"
      :title="$t('INSTAGRAM_AUTO_REPLY.DELETE.TITLE')"
      :message="$t('INSTAGRAM_AUTO_REPLY.DELETE.MESSAGE', { name: ruleToDelete?.name })"
      :confirm-text="$t('INSTAGRAM_AUTO_REPLY.DELETE.CONFIRM_BUTTON')"
      :reject-text="$t('INSTAGRAM_AUTO_REPLY.CANCEL_BUTTON_TEXT')"
      @close="showDeleteModal = false"
    />
  </SettingsLayout>
</template>
